import { useState, useEffect, useCallback, useRef } from 'react';
import { usePod } from '../hooks/usePod.jsx';
import { useCurrentUser } from '../hooks/useCurrentUser.jsx';
import ActivityService from '../services/ActivityService.js';

/**
 * PodMemberPicker — toggle chips for selecting which pod members you did something with.
 * Shows pod members (excluding current user) as emoji toggle buttons.
 * Persists selections as activity records via the activities API.
 *
 * Self-contained: creates its own ActivityService internally.
 * Just drop it in any detail modal.
 *
 * @param {Object} props
 * @param {string} props.itemId - PocketBase record ID of the item
 * @param {string} props.itemType - 'movie' | 'game' | 'book' | 'boardgame' | 'season'
 * @param {string} props.activityType - 'watched' | 'played' | 'read' | 'finished'
 * @param {string} [props.label] - Display label (e.g. "Watched with", "Played with")
 */
export default function PodMemberPicker({ itemId, altItemIds, itemType, itemTitle, activityType, label = 'With', hideLabel = false, onChanged }) {
    const { pod, allMembers } = usePod();
    const currentUser = useCurrentUser();
    const [selected, setSelected] = useState(new Set());
    const [activityId, setActivityId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const serviceRef = useRef(null);

    // Lazy-init ActivityService
    if (!serviceRef.current && currentUser?.id) {
        serviceRef.current = new ActivityService(currentUser.id);
    }

    // All pod members across all pods (exclude current user)
    const otherMembers = allMembers?.filter((m) => m.userId !== currentUser?.id) || [];

    // Load existing activity for this item on mount
    useEffect(() => {
        if (!serviceRef.current || !itemId || !pod) {
            setLoading(false);
            return;
        }
        let mounted = true;
        (async () => {
            try {
                const result = await serviceRef.current.list({ podId: pod.id, limit: 200 });
                const allIds = [itemId, ...(altItemIds || [])];
                const existing = (result.items || []).find(
                    (a) => allIds.includes(a.itemId) && a.itemType === itemType
                );
                if (mounted && existing) {
                    setActivityId(existing.id);
                    const others = (existing.participants || []).filter((id) => id !== currentUser?.id);
                    const initialSelected = new Set(others);
                    setSelected(initialSelected);
                    if (initialSelected.size > 0) onChanged?.(initialSelected);
                }
            } catch (_) {
                // Silently fail — start with nothing selected
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, [itemId, itemType, pod?.id, currentUser?.id]);

    const persist = useCallback(async (newSelected) => {
        if (!serviceRef.current || !pod || !currentUser) return;
        setSaving(true);
        try {
            const participants = [currentUser.id, ...newSelected];
            if (newSelected.size === 0) {
                if (activityId) {
                    await serviceRef.current.delete(activityId);
                    setActivityId(null);
                }
            } else if (activityId) {
                await serviceRef.current.delete(activityId);
                const result = await serviceRef.current.create({
                    type: activityType,
                    itemType,
                    itemId,
                    itemTitle: itemTitle || '',
                    participants,
                    pod: pod.id,
                    date: new Date().toISOString(),
                });
                setActivityId(result.id);
            } else {
                const result = await serviceRef.current.create({
                    type: activityType,
                    itemType,
                    itemId,
                    itemTitle: itemTitle || '',
                    participants,
                    pod: pod.id,
                    date: new Date().toISOString(),
                });
                setActivityId(result.id);
            }
            onChanged?.(newSelected);
        } catch (err) {
            console.error('Failed to save activity:', err);
        } finally {
            setSaving(false);
        }
    }, [activityId, pod, currentUser, activityType, itemType, itemId, onChanged]);

    const toggle = (userId) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(userId)) next.delete(userId);
            else next.add(userId);
            persist(next);
            return next;
        });
    };

    // Don't render if no other members across any pod
    if (otherMembers.length === 0) return null;
    if (loading) return null;

    if (hideLabel) {
        return (
            <div className="flex flex-wrap justify-end gap-1">
                {otherMembers.map((m) => {
                    const isSelected = selected.has(m.userId);
                    return (
                        <button
                            key={m.userId}
                            onClick={() => toggle(m.userId)}
                            disabled={saving}
                            className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs transition-all ${
                                isSelected
                                    ? 'bg-accent-primary/20 border border-accent-primary text-accent-primary'
                                    : 'bg-bg-secondary border border-border-subtle text-text-tertiary hover:text-text-secondary hover:bg-surface-hover'
                            } ${saving ? 'opacity-50' : ''}`}
                        >
                            <span className="text-sm leading-none">{m.emoji || '👤'}</span>
                            <span className="text-xs font-medium">{m.displayName || m.name}</span>
                        </button>
                    );
                })}
            </div>
        );
    }

    return (
        <div className="flex items-center justify-between min-h-[24px]">
            <span className="text-xs text-text-tertiary">{label}</span>
            <div className="flex flex-wrap justify-end gap-1">
                {otherMembers.map((m) => {
                    const isSelected = selected.has(m.userId);
                    return (
                        <button
                            key={m.userId}
                            onClick={() => toggle(m.userId)}
                            disabled={saving}
                            className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs transition-all ${
                                isSelected
                                    ? 'bg-accent-primary/20 border border-accent-primary text-accent-primary'
                                    : 'bg-bg-secondary border border-border-subtle text-text-tertiary hover:text-text-secondary hover:bg-surface-hover'
                            } ${saving ? 'opacity-50' : ''}`}
                        >
                            <span className="text-sm leading-none">{m.emoji || '👤'}</span>
                            <span className="text-xs font-medium">{m.displayName || m.name}</span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
